import React, { useState } from "react";
import { useDispatch } from 'react-redux';
import { addauth } from '../slices/authSlice.js';
import { APILogon, APIVerify2FA, APISetup2FA, APIVerifySetup2FA } from '../APIS/userAPI';
import { showSuccess, showError, showWarning } from '../helpers/notifications';
import logo from '../assets/logo.png';
import '../css/logon.css';

const Logon = (props) => {
  const dispatch = useDispatch();

  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [requires2FASetup, setRequires2FASetup] = useState(false);
  const [challengeId, setChallengeId] = useState(null);
  const [method, setMethod] = useState(null); // 'email', 'sms', or 'totp'
  const [destination, setDestination] = useState(null); // Masked email/phone
  const [userId, setUserId] = useState(null);
  const [setupToken, setSetupToken] = useState(null);

  // 2FA verification state
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [deviceName, setDeviceName] = useState('');

  // 2FA setup state
  const [selectedMethod, setSelectedMethod] = useState('email');
  const [phone, setPhone] = useState('');
  const [setupStep, setSetupStep] = useState('choose'); // 'choose', 'verify', 'backup'
  const [qrUrl, setQrUrl] = useState(null);
  const [secret, setSecret] = useState(null);
  const [backupCodes, setBackupCodes] = useState([]);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState(null);

  /**
   * Handle initial login
   */
  const DoLogon = async () => {
    setError('');
    setIsLoading(true);

    try {
      // Get stored device token if exists
      const deviceToken = localStorage.getItem('deviceToken');

      const ret = await APILogon(username, password, deviceToken);

      if (!ret.status) {
        setError(ret.err || 'Invalid login credentials');
        return;
      }

      // Case 1: Login successful (trusted device or no 2FA)
      if (ret.data && ret.data.token) {
        handleLoginSuccess(ret.data);
        return;
      }

      // Case 2: 2FA required
      if (ret.requires2FA) {
        setRequires2FA(true);
        setChallengeId(ret.challengeId);
        setMethod(ret.method);
        setDestination(ret.destination);
        setUserId(ret.userId);
        showSuccess(`A verification code has been sent to ${ret.destination}`);
        return;
      }

      // Case 3: 2FA setup required
      if (ret.requires2FASetup) {
        setRequires2FASetup(true);
        setSetupToken(ret.setupToken);
        setUserId(ret.userId);
        showWarning('Please set up two-factor authentication to continue');
        return;
      }

    } catch (err) {
      setError('Network error. Please try again.');
      showError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle 2FA code verification
   */
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const ret = await APIVerify2FA(userId, code, challengeId, trustDevice, trustDevice ? deviceName : null);

      if (!ret.status) {
        setError(ret.err || 'Invalid code');
        setRemainingAttempts(ret.remainingAttempts);

        if (ret.restartRequired) {
          showError('Too many failed attempts. Please login again.');
          resetToLogin();
        } else {
          showError(ret.err || 'Invalid code');
        }
        return;
      }

      // Success - store device token if provided
      if (ret.data.deviceToken) {
        localStorage.setItem('deviceToken', ret.data.deviceToken);
        showSuccess('Device trusted for 30 days');
      }

      // Show warning if backup code was used
      if (ret.data.warning) {
        showWarning(ret.data.warning);
      }

      handleLoginSuccess(ret.data);

    } catch (err) {
      setError('Network error. Please try again.');
      showError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle 2FA setup - step 1: choose method and initiate
   */
  const handleSetup2FAMethod = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const ret = await APISetup2FA(setupToken, selectedMethod, selectedMethod === 'sms' ? phone : null);

      if (!ret.status) {
        setError(ret.err || 'Setup failed');
        showError(ret.err || 'Setup failed');
        return;
      }

      // Handle different method responses
      if (selectedMethod === 'totp') {
        setQrUrl(ret.qrUrl);
        setSecret(ret.secret);
      } else {
        setChallengeId(ret.challengeId);
        setDestination(ret.destination);
        showSuccess(`Code sent to ${ret.destination}`);
      }

      setSetupStep('verify');

    } catch (err) {
      setError('Network error. Please try again.');
      showError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle 2FA setup - step 2: verify setup code
   */
  const handleVerifySetup = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const ret = await APIVerifySetup2FA(setupToken, code, selectedMethod !== 'totp' ? challengeId : null);

      if (!ret.status) {
        setError(ret.err || 'Invalid code');
        showError(ret.err || 'Invalid code');
        return;
      }

      // Store backup codes
      if (ret.data.backupCodes) {
        setBackupCodes(ret.data.backupCodes);
        setSetupStep('backup');
      } else {
        // If no backup codes, complete login
        handleLoginSuccess(ret.data);
      }

    } catch (err) {
      setError('Network error. Please try again.');
      showError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Complete login and store user data
   */
  const handleLoginSuccess = (data) => {
    // Store JWT token
    localStorage.setItem('token', data.token);

    // Update Redux store
    const loggedon = { ...data, loggedon: true };
    dispatch(addauth(loggedon));

    showSuccess(`Welcome back, ${data.name || data.email}!`);
  };

  /**
   * Reset to initial login screen
   */
  const resetToLogin = () => {
    setRequires2FA(false);
    setRequires2FASetup(false);
    setCode('');
    setTrustDevice(false);
    setDeviceName('');
    setRemainingAttempts(null);
    setError('');
  };

  /**
   * Complete 2FA setup and login
   */
  const completeSetup = () => {
    // You should download/save backup codes before completing
    const data = { token: localStorage.getItem('token') };
    // handleLoginSuccess will be called after setup verification
    showSuccess('2FA setup complete!');
    window.location.reload(); // Reload to trigger authenticated state
  };

  // Render: Backup Codes Display
  if (setupStep === 'backup' && backupCodes.length > 0) {
    return (
      <div className='logonForm'>
        <img src={logo} alt='Greenfield Recovery Ltd' className="logo" width="150" /><br/>
        <h2>Save Your Backup Codes</h2>
        <p style={{fontSize: '14px', marginBottom: '15px'}}>
          Store these backup codes in a safe place. Each can be used once if you lose access to your 2FA device.
        </p>
        <div style={{
          backgroundColor: '#f5f5f5',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '15px',
          fontFamily: 'monospace',
          fontSize: '14px'
        }}>
          {backupCodes.map((code, index) => (
            <div key={index} style={{padding: '5px'}}>{code}</div>
          ))}
        </div>
        <button className='grnbtn' onClick={completeSetup}>
          I have saved my backup codes
        </button>
      </div>
    );
  }

  // Render: 2FA Setup - Verify Code
  if (requires2FASetup && setupStep === 'verify') {
    return (
      <div className='logonForm'>
        <img src={logo} alt='Greenfield Recovery Ltd' className="logo" width="150" /><br/>
        <h2>Verify Setup Code</h2>

        {selectedMethod === 'totp' && qrUrl && (
          <div style={{marginBottom: '20px'}}>
            <p style={{fontSize: '14px', marginBottom: '10px'}}>
              Scan this QR code with your authenticator app:
            </p>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(qrUrl)}&size=200x200`}
              alt="QR Code"
              style={{border: '1px solid #ccc', padding: '10px'}}
            />
            <p style={{fontSize: '12px', marginTop: '10px', fontFamily: 'monospace'}}>
              Or enter manually: {secret}
            </p>
          </div>
        )}

        {selectedMethod !== 'totp' && (
          <p style={{fontSize: '14px', marginBottom: '15px'}}>
            Enter the code sent to {destination}
          </p>
        )}

        <form onSubmit={handleVerifySetup}>
          <label className='label'>Verification Code</label>
          <input
            className='username'
            type='text'
            placeholder="Enter 6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            disabled={isLoading}
            autoFocus
          />

          {error && <p style={{color: 'red', fontSize: '14px'}}>{error}</p>}

          <input
            type='submit'
            className='grnbtn'
            value={isLoading ? 'Verifying...' : 'Verify and Complete Setup'}
            disabled={isLoading || code.length !== 6}
          />
          <button
            type='button'
            className='grnbtn'
            onClick={() => setSetupStep('choose')}
            style={{marginTop: '10px', backgroundColor: '#999'}}
          >
            Back
          </button>
        </form>
      </div>
    );
  }

  // Render: 2FA Setup - Choose Method
  if (requires2FASetup && setupStep === 'choose') {
    return (
      <div className='logonForm'>
        <img src={logo} alt='Greenfield Recovery Ltd' className="logo" width="150" /><br/>
        <h2>Set Up Two-Factor Authentication</h2>
        <p style={{fontSize: '14px', marginBottom: '20px'}}>
          Choose your preferred 2FA method:
        </p>

        <form onSubmit={handleSetup2FAMethod}>
          <div style={{textAlign: 'left', marginBottom: '20px'}}>
            <label style={{display: 'block', marginBottom: '10px', cursor: 'pointer'}}>
              <input
                type='radio'
                value='email'
                checked={selectedMethod === 'email'}
                onChange={(e) => setSelectedMethod(e.target.value)}
              />
              <span style={{marginLeft: '10px'}}>Email - Code sent to your email</span>
            </label>

            <label style={{display: 'block', marginBottom: '10px', cursor: 'pointer'}}>
              <input
                type='radio'
                value='sms'
                checked={selectedMethod === 'sms'}
                onChange={(e) => setSelectedMethod(e.target.value)}
              />
              <span style={{marginLeft: '10px'}}>SMS - Code sent to your phone</span>
            </label>

            <label style={{display: 'block', marginBottom: '10px', cursor: 'pointer'}}>
              <input
                type='radio'
                value='totp'
                checked={selectedMethod === 'totp'}
                onChange={(e) => setSelectedMethod(e.target.value)}
              />
              <span style={{marginLeft: '10px'}}>Authenticator App - Google Authenticator, Authy, etc.</span>
            </label>
          </div>

          {selectedMethod === 'sms' && (
            <>
              <label className='label'>Phone Number</label>
              <input
                className='username'
                type='tel'
                placeholder="+44 7123 456789"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={isLoading}
                required
              />
            </>
          )}

          {error && <p style={{color: 'red', fontSize: '14px'}}>{error}</p>}

          <input
            type='submit'
            className='grnbtn'
            value={isLoading ? 'Setting up...' : 'Continue'}
            disabled={isLoading || (selectedMethod === 'sms' && !phone)}
          />
        </form>
      </div>
    );
  }

  // Render: 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className='logonForm'>
        <img src={logo} alt='Greenfield Recovery Ltd' className="logo" width="150" /><br/>
        <h2>Two-Factor Authentication</h2>

        <p style={{fontSize: '14px', marginBottom: '20px'}}>
          {method === 'email' && `A code has been sent to ${destination}`}
          {method === 'sms' && `A code has been sent to ${destination}`}
          {method === 'totp' && 'Enter the code from your authenticator app'}
        </p>

        <form onSubmit={handleVerify2FA}>
          <label className='label'>Verification Code</label>
          <input
            className='username'
            type='text'
            placeholder="Enter 6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            disabled={isLoading}
            autoFocus
          />

          <div style={{textAlign: 'left', margin: '15px 0'}}>
            <label style={{cursor: 'pointer', fontSize: '14px'}}>
              <input
                type='checkbox'
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
              />
              <span style={{marginLeft: '8px'}}>Trust this device for 30 days</span>
            </label>
          </div>

          {trustDevice && (
            <>
              <label className='label'>Device Name (optional)</label>
              <input
                className='username'
                type='text'
                placeholder="e.g., My Laptop"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                disabled={isLoading}
              />
            </>
          )}

          {error && <p style={{color: 'red', fontSize: '14px'}}>{error}</p>}
          {remainingAttempts !== null && (
            <p style={{color: 'orange', fontSize: '14px'}}>
              {remainingAttempts} attempt{remainingAttempts !== 1 ? 's' : ''} remaining
            </p>
          )}

          <input
            type='submit'
            className='grnbtn'
            value={isLoading ? 'Verifying...' : 'Verify'}
            disabled={isLoading || code.length !== 6}
          />
          <button
            type='button'
            className='grnbtn'
            onClick={resetToLogin}
            style={{marginTop: '10px', backgroundColor: '#999'}}
            disabled={isLoading}
          >
            Back to Login
          </button>
        </form>
      </div>
    );
  }

  // Render: Initial Login Screen
  return (
    <div className='logonForm'>
      <img src={logo} alt='Greenfield Recovery Ltd' className="logo" width="150" /><br/>

      <label className='label' id="lbluserName" htmlFor='username'>Username</label>
      <input
        className='username'
        type='text'
        id="username"
        name="username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        disabled={isLoading}
        autoComplete="username"
      />

      <label className='label' id="lblPassword" htmlFor='password'>Password</label>
      <input
        className='password'
        type='password'
        id="password"
        name="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={isLoading}
        onKeyDown={(e) => e.key === 'Enter' && DoLogon()}
        autoComplete="current-password"
      />

      <br/>&nbsp;<br/>

      <input
        type='button'
        className='grnbtn'
        onClick={DoLogon}
        value={isLoading ? 'Logging in...' : 'Logon'}
        disabled={isLoading || !username || !password}
      />

      {error && <p style={{color: 'red', fontSize: '14px', marginTop: '10px'}}>{error}</p>}
    </div>
  );
};

export default Logon;
